import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { makeStyles, Popover } from '@material-ui/core';
import Link from 'next/link';
import { StyledTabTitle, SubTitle } from '@features/ui/components/Tabs/styles';
import { TabProps } from '@features/ui/components/Tabs';
import Icon, { ICON_NAME } from '@features/ui/components/Icon';
import Card from '@features/ui/components/Card';

const Mobile = ({ tabs, currentIndex, className }: TabProps) => {
  const currentTab = tabs[currentIndex];
  const [anchorEl, setAnchorEl] = useState(null);

  const popOverClasses = usePopOverStyles();

  const handleTabClick = (tab) => {
    if (tab.onClick && !tab.disabled) {
      tab.onClick();
    }

    if (!tab.disabled) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <Container className={className}>
      <Card onClick={handleClick}>
        <StyledTabTitle>
          {currentTab.title}
          {currentTab.subTitle && <SubTitle>{currentTab.subTitle}</SubTitle>}
        </StyledTabTitle>

        <StyledIcon
          name={ICON_NAME.expandMore}
          size="30px"
          color="var(--color-orange)"
        />
      </Card>
      <Popover
        open={open}
        classes={popOverClasses}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={0}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {tabs.map((tab, index) => {
          const title = (
            <StyledTabTitle disabled={tab.disabled}>
              {tab.title}
              {tab.subTitle && <SubTitle>{tab.subTitle}</SubTitle>}
            </StyledTabTitle>
          );

          return (
            <StyledPopOverCard
              key={tab.key}
              isCurrent={index === currentIndex}
              onClick={() => handleTabClick(tab)}
              disabled={tab.disabled}
            >
              {tab.disabled ? <>{title}</> : (
                <Link href={tab.href ?? '#'}>
                  <a data-qa={`ui-tab-item-${tab.qaId}`}>{title}</a>
                </Link>
              )}
            </StyledPopOverCard>
          );
        })}
      </Popover>
    </Container>
  );
};

const usePopOverStyles = makeStyles(() => ({
  paper: {
    boxShadow: '0 2px 12px 0 rgba(128, 122, 122, 0.2) ',
    border: '1px solid var(--color-grey1)',
    width: '100%',
  },
  root: { width: '100%' },
}));

const indicator = css`
  ::after {
    position: absolute;
    content: "";
    height: 4px;
    width: 100%;
    bottom: 0px;
    background: var(--color-blue);
  }
`;

const StyledPopOverCard = styled.div<{ isCurrent?: boolean, disabled?: boolean }>`
  width: 100%;
  height: 84px;
  position: relative;
  ${({ isCurrent }) => isCurrent && indicator}
  ${({ disabled }) => !disabled && css`
    :hover {
      ${indicator};
      ::after {
        background: var(--color-blue-hover);
      }
    }
    cursor: pointer;
  `}
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  right: 30px;
  top: 30px;
`;

const Container = styled.div`
  display: flex;
  height: 84px;
  width: 100%;
  position: relative;
  ${indicator};
`;

export default Mobile;
